import { useState, type FormEvent } from "react";

const SUBMIT_URL = "https://readdy.ai/api/form/dahamr3p14a1h3mr6h2g";

type Status = "idle" | "submitting" | "success" | "error";

const topicOptions = [
  "娱乐圈",
  "网红圈",
  "情感瓜",
  "职场瓜",
  "科技圈",
  "综艺瓜",
  "其他 / 不确定",
];

export default function SubmitForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("contact_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setSuccessMsg("已收到你的线索，感谢分享。");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "contact_alt") return;
      const text = typeof value === "string" ? value : "";
      if (text.trim() === "") return;
      payload.append(key, text);
    });

    setStatus("submitting");
    setFormError("");

    try {
      const response = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }

      const meta = (parsed?.meta ?? null) as Record<string, unknown> | null;
      const serverMsgRaw =
        meta?.message ?? parsed?.message ?? meta?.detail ?? responseText ?? "";
      const serverMsg = typeof serverMsgRaw === "string" ? serverMsgRaw : "";
      const code = typeof parsed?.code === "string" ? parsed.code : "";
      const isSpam = serverMsg.toLowerCase().includes("spam");

      if (!response.ok || code !== "OK" || isSpam) {
        setStatus("error");
        setFormError(
          serverMsg && !isSpam
            ? serverMsg
            : "提交未成功，请检查内容后重试，或稍后再来。"
        );
        return;
      }

      setStatus("success");
      setSuccessMsg("已收到你的线索，我们会在核实后整理成时间线，感谢分享！");
      form.reset();
    } catch {
      setStatus("error");
      setFormError("网络异常，提交未成功，请稍后重试。");
    }
  };

  return (
    <form
      id="submit"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg bg-background-50 border border-background-200 p-5 md:p-8 scroll-mt-24"
    >
      <div className="flex items-center gap-3 mb-6">
        <span className="w-11 h-11 rounded-md bg-primary-500 text-background-50 flex items-center justify-center">
          <i className="ri-megaphone-line text-xl" aria-hidden="true"></i>
        </span>
        <div>
          <h3 className="text-lg font-bold text-foreground-950">爆料投稿</h3>
          <p className="text-xs text-foreground-400 mt-0.5">
            除邮箱外不收集任何身份信息，昵称可随意填写
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
        <div>
          <label
            htmlFor="nickname"
            className="block text-sm text-foreground-700 mb-2"
          >
            你的昵称
          </label>
          <input
            id="nickname"
            name="nickname"
            type="text"
            required
            placeholder="例如：半夜蹲瓜的梨"
            className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
          />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm text-foreground-700 mb-2">
            邮箱（可选，用于后续联系）
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors"
          />
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="topic" className="block text-sm text-foreground-700 mb-2">
          线索所属圈层
        </label>
        <select
          id="topic"
          name="topic"
          defaultValue={topicOptions[0]}
          className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 outline-none focus:border-primary-300 transition-colors cursor-pointer"
        >
          {topicOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-5">
        <label htmlFor="content" className="block text-sm text-foreground-700 mb-2">
          线索内容
        </label>
        <textarea
          id="content"
          name="content"
          required
          maxLength={500}
          rows={5}
          placeholder="简单说明时间、人物圈层与可公开求证的信息来源，最多 500 字"
          className="w-full px-3 py-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-colors resize-none"
        ></textarea>
        <p className="mt-1.5 text-xs text-foreground-400">最多 500 字</p>
      </div>

      <div className="layout-aux-note" aria-hidden="true">
        <label htmlFor="contact_alt">备用联系方式</label>
        <input
          id="contact_alt"
          name="contact_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex items-center justify-center gap-2 h-11 px-7 rounded-full bg-primary-500 text-background-50 text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <i
            className={
              status === "submitting" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"
            }
            aria-hidden="true"
          ></i>
          {status === "submitting" ? "提交中…" : "提交线索"}
        </button>
        <p className="text-xs text-foreground-400">
          提交即表示你同意我们整理并用于内容创作
        </p>
      </div>

      {status === "success" && (
        <div className="mt-5 flex items-start gap-2 rounded-md bg-secondary-100 border border-secondary-200 px-4 py-3">
          <i
            className="ri-checkbox-circle-line text-secondary-700 mt-0.5"
            aria-hidden="true"
          ></i>
          <p className="text-sm text-secondary-900">{successMsg}</p>
        </div>
      )}

      {status === "error" && (
        <div className="mt-5 flex items-start gap-2 rounded-md bg-primary-50 border border-primary-200 px-4 py-3">
          <i
            className="ri-error-warning-line text-primary-600 mt-0.5"
            aria-hidden="true"
          ></i>
          <p className="text-sm text-primary-800">{formError}</p>
        </div>
      )}
    </form>
  );
}